'use client'

import React, { useState, useEffect } from 'react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { BrandCard } from './brands/BrandCard'
import { BrandDashboard } from './brands/BrandDashboard'
import { CreateBrandModal } from './brands/CreateBrandModal'
import { Alert, AlertDescription } from './ui/alert'
import { 
  Plus, 
  Search, 
  Filter, 
  Sparkles, 
  TrendingUp, 
  Users, 
  Globe,
  Settings,
  User as UserIcon,
  LogOut,
  Bell,
  CheckCircle
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { mockProfileService, mockBrandService, Brand, Profile, User } from '../utils/mockData'

interface DashboardProps {
  user: User
  onLogout: () => void
}

export function Dashboard({ user, onLogout }: DashboardProps) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [brands, setBrands] = useState<Brand[]>([])
  const [filteredBrands, setFilteredBrands] = useState<Brand[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateBrandModal, setShowCreateBrandModal] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)

  useEffect(() => {
    initializeUserData()
  }, [])

  useEffect(() => {
    // Filter brands based on search query
    if (searchQuery.trim() === '') {
      setFilteredBrands(brands)
    } else {
      const filtered = brands.filter(brand =>
        brand.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (brand.description && brand.description.toLowerCase().includes(searchQuery.toLowerCase()))
      )
      setFilteredBrands(filtered)
    }
  }, [searchQuery, brands])

  const initializeUserData = async () => {
    try {
      setIsInitializing(true)
      setError(null)

      // Fetch user profile
      await fetchProfile()
      
      // Fetch user brands
      await fetchBrands()

    } catch (error: any) {
      console.error('Error initializing user data:', error)
      setError(error.message || 'Failed to initialize user data')
    } finally {
      setIsInitializing(false)
      setIsLoading(false)
    }
  }

  const fetchProfile = async () => {
    try {
      const { profile, error } = await mockProfileService.getProfile()
      
      if (error) {
        throw new Error(error)
      }
      
      setProfile(profile)
    } catch (error) {
      console.error('Error fetching profile:', error)
      throw error
    }
  }

  const fetchBrands = async () => {
    try {
      const { brands, error } = await mockBrandService.getBrands()
      
      if (error) {
        throw new Error(error)
      }
      
      // Sort by last_activity (most recent first)
      const sortedBrands = brands.sort((a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime())
      setBrands(sortedBrands)
    } catch (error) {
      console.error('Error fetching brands:', error)
      throw error
    }
  }

  const handleViewBrand = (brand: Brand) => {
    setSelectedBrand(brand)
  }

  const handleBackToDashboard = () => {
    setSelectedBrand(null)
  }

  const handleEditBrand = (brand: Brand) => {
    console.log('Edit brand:', brand)
    // TODO: Navigate to brand settings
  }

  const handleInviteTeam = (brand: Brand) => {
    console.log('Invite team to brand:', brand)
    // TODO: Open team invitation modal
  }

  const handleViewPublic = (brand: Brand) => {
    window.open(`/brand/${brand.slug}`, '_blank')
  }

  const handleDeleteBrand = async (brand: Brand) => {
    if (!confirm(`Are you sure you want to delete "${brand.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      const { error } = await mockBrandService.deleteBrand(brand.id)
      
      if (error) {
        throw new Error(error)
      }

      // Remove brand from local state
      setBrands(prevBrands => prevBrands.filter(b => b.id !== brand.id))
      // If currently viewing this brand, go back to dashboard
      if (selectedBrand?.id === brand.id) {
        setSelectedBrand(null)
      }
    } catch (error) {
      console.error('Error deleting brand:', error)
      alert('Failed to delete brand. Please try again.')
    }
  }

  const handleCreateBrand = () => {
    setShowCreateBrandModal(true)
  }

  const handleBrandCreated = (newBrand: Brand) => {
    setBrands(prevBrands => [newBrand, ...prevBrands])
    setShowCreateBrandModal(false)
  }

  if (isLoading || isInitializing) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-white animate-pulse" />
          </div>
          <p className="text-slate-600">
            {isInitializing ? 'Setting up your workspace...' : 'Loading your brands...'}
          </p>
        </div>
      </div>
    )
  }

  // If a brand is selected, show the brand dashboard
  if (selectedBrand) {
    return (
      <BrandDashboard 
        brand={selectedBrand}
        onBack={handleBackToDashboard}
      />
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                BrandZit
              </span>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              <a href="#" className="text-slate-600 hover:text-slate-900 transition-colors">
                Dashboard
              </a>
              <a href="#" className="text-slate-600 hover:text-slate-900 transition-colors">
                Templates
              </a>
              <a href="#" className="text-slate-600 hover:text-slate-900 transition-colors">
                Help
              </a>
            </nav>
          </div>

          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm">
              <Bell className="h-4 w-4" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={profile?.avatar_url || ''} alt={profile?.full_name || ''} />
                    <AvatarFallback>
                      {profile?.full_name ? profile.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : user.email?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                    <p className="font-medium">{profile?.full_name || 'User'}</p>
                    <p className="text-xs text-slate-600">{profile?.email || user.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <UserIcon className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Error Alert */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertDescription className="text-red-800">
              <div className="space-y-2">
                <p className="font-medium">Error</p>
                <p>{error}</p>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => {
                    setError(null)
                    initializeUserData()
                  }}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Success Alert for Setup */}
        {!error && profile && brands.length === 0 && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <div className="space-y-1">
                <p className="font-medium">✨ BrandZit is Ready!</p>
                <p>Your workspace has been set up successfully. You can now create and manage brands with AI-powered tools.</p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Welcome back, {profile?.full_name?.split(' ')[0] || user.email?.split('@')[0] || 'there'}! 👋
            </h1>
            <p className="text-slate-600">
              Manage your brands and continue building with AI-powered tools.
            </p>
          </div>
          <Button 
            onClick={handleCreateBrand}
            className="mt-4 sm:mt-0 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Brand
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant="outline" className="flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Filter</span>
          </Button>
        </div>

        {/* Brands Grid */}
        {filteredBrands.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBrands.map((brand) => (
              <BrandCard
                key={brand.id}
                brand={brand}
                onView={handleViewBrand}
                onEdit={handleEditBrand}
                onInvite={handleInviteTeam}
                onViewPublic={handleViewPublic}
                onDelete={handleDeleteBrand}
              />
            ))}
          </div>
        ) : brands.length === 0 ? (
          // Empty state for no brands
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-blue-600" />
              </div>
              <CardTitle className="text-xl mb-2">Create Your First Brand</CardTitle>
              <CardDescription className="mb-6 max-w-md mx-auto">
                Get started with AI-powered brand analysis and guidelines. 
                Our smart onboarding will help you build a professional brand in minutes.
              </CardDescription>
              <Button 
                onClick={handleCreateBrand}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Brand
              </Button>
            </CardContent>
          </Card>
        ) : (
          // Empty state for search with no results
          <Card className="text-center py-12">
            <CardContent>
              <Search className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <CardTitle className="text-lg mb-2">No brands found</CardTitle>
              <CardDescription>
                Try adjusting your search terms or create a new brand.
              </CardDescription>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Brand Modal */}
      <CreateBrandModal
        isOpen={showCreateBrandModal}
        onClose={() => setShowCreateBrandModal(false)}
        onSuccess={handleBrandCreated}
      />
    </div>
  )
}